import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode, withModifiers as _withModifiers, normalizeClass as _normalizeClass, renderSlot as _renderSlot, createCommentVNode as _createCommentVNode, normalizeStyle as _normalizeStyle, Teleport as _Teleport, createBlock as _createBlock } from "vue"

const _hoisted_1 = ["onMousedown"]
const _hoisted_2 = { class: "mosta-context-menu__content" }

import { computed, ref, watch, onBeforeUnmount, nextTick } from 'vue';
import { ownsEscape } from '@/composables/overlayStack';


export default /*@__PURE__*/_defineComponent({
  __name: 'MostaContextMenu',
  props: {
    visible: { type: Boolean },
    position: {},
    minWidth: { default: 200 },
    menuClass: { default: '' },
    backdropCursor: { default: 'default' },
    disableBackdropPointer: { type: Boolean, default: false },
    zIndex: { default: 9999 },
    resizable: { type: Boolean, default: false },
    defaultWidth: { default: null },
    defaultHeight: { default: null },
    maxWidth: { default: 2000 },
    minHeight: { default: 120 },
    maxHeight: { default: 2000 },
    closeOnEsc: { type: Boolean, default: false }
  },
  emits: ["close", "menuEnter", "menuLeave", "contentResize"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const resizeCorners = ['tl', 'tr', 'bl', 'br'] as const;

const menuEl = ref<HTMLElement | null>(null);

// при resizable размер и позицию (при перетаскивании верх/лево) держим внутри компонента,
// позиция по умолчанию берётся из props.position, пока пользователь не растянул меню
const userWidth = ref<number | null>(null);
const userHeight = ref<number | null>(null);
const posTop = ref<string | null>(null);
const posLeft = ref<string | null>(null);
const isResizing = ref(false);
const viewportH = ref(typeof window !== 'undefined' ? window.innerHeight : 0);

function updateViewport() {
  viewportH.value = window.innerHeight;
}

// содержимое меню меняет размер асинхронно (загрузка данных, анимация вкладок),
// поэтому о каждом изменении сообщаем наружу - там пересчитывается позиция и флип
let contentObserver: ResizeObserver | null = null;

function startObserveContent() {
  stopObserveContent();
  const el = menuEl.value;
  if (!el || typeof ResizeObserver === 'undefined') return;
  contentObserver = new ResizeObserver(() => {
    if (isResizing.value) return;
    emit('contentResize');
  });
  contentObserver.observe(el);
}

function stopObserveContent() {
  contentObserver?.disconnect();
  contentObserver = null;
}

watch(
  () => props.visible,
  (v) => {
    if (v) {
      updateViewport();
      window.addEventListener('resize', updateViewport);
      userWidth.value = props.resizable ? props.defaultWidth : null;
      userHeight.value = props.resizable ? props.defaultHeight : null;
      posTop.value = null;
      posLeft.value = null;
      nextTick(startObserveContent);
    } else {
      window.removeEventListener('resize', updateViewport);
      stopObserveContent();
    }
  }
);

// внешнее перепозиционирование (флип у вызывающего) сбрасывает ручной сдвиг, кроме момента ресайза
watch(
  () => props.position,
  () => {
    if (!isResizing.value) {
      posTop.value = null;
      posLeft.value = null;
    }
  },
  { deep: true }
);

const menuStyle = computed(() => {
  const s: Record<string, string | number> = {
    top: posTop.value ?? (props.position?.top ?? '0px'),
    left: posLeft.value ?? (props.position?.left ?? '0px'),
    minWidth: `${props.minWidth}px`,
    zIndex: props.zIndex + 1,
  };
  if (props.resizable) {
    // потолки нужны и без ручного ресайза: при размере по контенту они держат меню в экране
    s.minHeight = `${props.minHeight}px`;
    s.maxWidth = `${props.maxWidth}px`;
    s.maxHeight = `${Math.min(
      props.maxHeight,
      Math.max(props.minHeight, viewportH.value - 16)
    )}px`;
    if (userWidth.value != null) s.width = `${userWidth.value}px`;
    if (userHeight.value != null) s.height = `${userHeight.value}px`;
  }
  return s;
});

const backdropStyle = computed(() => ({
  cursor: props.backdropCursor,
  pointerEvents: props.disableBackdropPointer ? 'none' : 'auto',
  zIndex: props.zIndex,
}));

function clamp(v: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, v));
}

let startX = 0;
let startY = 0;
let startWidth = 0;
let startHeight = 0;
let startTop = 0;
let startLeft = 0;
let activeCorner = '';

function startResize(corner: string, e: MouseEvent) {
  const el = menuEl.value;
  if (!el) return;
  const rect = el.getBoundingClientRect();
  activeCorner = corner;
  startX = e.clientX;
  startY = e.clientY;
  startWidth = rect.width;
  startHeight = rect.height;
  startTop = rect.top;
  startLeft = rect.left;
  isResizing.value = true;
  document.addEventListener('mousemove', onResizeMove);
  document.addEventListener('mouseup', onResizeEnd);
}

function onResizeMove(e: MouseEvent) {
  const dx = e.clientX - startX;
  const dy = e.clientY - startY;
  const right = activeCorner.includes('r');
  const bottom = activeCorner.includes('b');
  let width: number;
  let height: number;
  let top = startTop;
  let left = startLeft;
  if (right) {
    width = clamp(startWidth + dx, props.minWidth, props.maxWidth);
  } else {
    width = clamp(startWidth - dx, props.minWidth, props.maxWidth);
    left = startLeft + (startWidth - width);
  }
  if (bottom) {
    height = clamp(startHeight + dy, props.minHeight, props.maxHeight);
  } else {
    height = clamp(startHeight - dy, props.minHeight, props.maxHeight);
    top = startTop + (startHeight - height);
  }
  userWidth.value = width;
  userHeight.value = height;
  posLeft.value = `${left}px`;
  posTop.value = `${top}px`;
}

function onResizeEnd() {
  isResizing.value = false;
  document.removeEventListener('mousemove', onResizeMove);
  document.removeEventListener('mouseup', onResizeEnd);
}

// Esc обрабатываем только по явному запросу потребителя: у части меню закрытие
// по Esc занято внешней логикой. Пока поверх открыт диалог подтверждения, Esc
// принадлежит ему, иначе одно нажатие закрыло бы сразу оба окна. По той же причине
// Esc забирает только верхний слой: вложенное меню, q-dialog и попап q-select
// закрываются сами (Quasar - на keyup, уже после нашего keydown), и такое событие
// не глушим, чтобы оно до них дошло.
function onEscKeydown(e: KeyboardEvent) {
  if (e.key !== 'Escape') return;
  if (document.querySelector('.confirm-popup')) return;
  if (!ownsEscape(menuEl.value)) {
    return;
  }
  e.stopPropagation();
  emit('close');
}

function stopEscListener() {
  document.removeEventListener('keydown', onEscKeydown, true);
}

watch(
  () => props.visible && props.closeOnEsc,
  (active) => {
    stopEscListener();
    if (active) {
      document.addEventListener('keydown', onEscKeydown, true);
    }
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  document.removeEventListener('mousemove', onResizeMove);
  document.removeEventListener('mouseup', onResizeEnd);
  window.removeEventListener('resize', updateViewport);
  stopObserveContent();
  stopEscListener();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
    (_ctx.visible)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          class: "mosta-context-menu-backdrop",
          style: _normalizeStyle(backdropStyle.value),
          onClick: _cache[3] || (_cache[3] = ($event: any) => (emit('close')))
        }, [
          _createElementVNode("div", {
            ref_key: "menuEl",
            ref: menuEl,
            class: _normalizeClass([
          'mosta-context-menu',
          _ctx.menuClass,
          {
            'mosta-context-menu--resizable': _ctx.resizable,
            'mosta-context-menu--resizing': isResizing.value,
          },
        ]),
            style: _normalizeStyle(menuStyle.value),
            onClick: _cache[0] || (_cache[0] = _withModifiers(() => {}, ["stop"])),
            onMouseenter: _cache[1] || (_cache[1] = ($event: any) => (emit('menuEnter'))),
            onMouseleave: _cache[2] || (_cache[2] = ($event: any) => (emit('menuLeave')))
          }, [
            (_ctx.resizable)
              ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                  (_openBlock(), _createElementBlock(_Fragment, null, _renderList(resizeCorners, (corner) => {
                    return _createElementVNode("div", {
                      key: corner,
                      class: _normalizeClass([
              'mosta-context-menu__resize-handle',
              `mosta-context-menu__resize-handle--${corner}`,
            ]),
                      onMousedown: _withModifiers(($event: any) => (startResize(corner, $event)), ["prevent","stop"])
                    }, _cache[4] || (_cache[4] = [
                      _createElementVNode("svg", {
                        viewBox: "0 0 10 10",
                        class: "mosta-context-menu__resize-icon"
                      }, [
                        _createElementVNode("path", {
                          d: "M3 3L7 7M5 7H7V5",
                          stroke: "currentColor",
                          "stroke-width": "1.5",
                          fill: "none",
                          "stroke-linecap": "round",
                          "stroke-linejoin": "round"
                        })
                      ], -1)
                    ]), 42, _hoisted_1)
                  }), 64)),
                  _createElementVNode("div", _hoisted_2, [
                    _renderSlot(_ctx.$slots, "default")
                  ])
                ], 64))
              : _renderSlot(_ctx.$slots, "default", { key: 1 })
          ], 38)
        ], 4))
      : _createCommentVNode("", true)
  ]))
}
}

})